import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  SAFETY_ROUND_TEMPLATE,
  HANDOVER_SAFETY_ROUND_TEMPLATE,
  SAFETY_ROUND_STATUS_LABELS,
  SAFETY_ROUND_TYPE_LABELS,
  type SafetyRoundStatus,
  type SafetyRoundType,
} from "@/lib/basProcess/constants";
import { HardHat, Plus, Trash2, Download, CheckCircle2 } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import { generateSafetyRoundWordDocument } from "@/lib/documentGenerator/skyddsrondWordGenerator";
import { OfflineBanner } from "@/components/BasProcess/OfflineBanner";
import {
  cacheGet,

  cacheSet,
  newLocalId,
  offlineDelete,
  offlineInsert,
  offlineUpdate,
} from "@/lib/offline/queue";
import { AiPolishTextarea } from "@/components/common/AiPolishTextarea";

interface Props {
  projectId: string;
}

interface Round {
  id: string;
  name: string;
  round_type: string;
  round_date: string | null;
  area: string | null;
  participants: string | null;
  status: string;
  notes: string | null;
  stage_id: string | null;
}

interface Item {
  id: string;
  round_id: string;
  section: string;
  item_key: string;
  control_point: string;
  legal_reference: string | null;
  status: string;
  responsible: string | null;
  due_date: string | null;
  comment: string | null;
  sort_order: number;
}

export const SafetyRounds: React.FC<Props> = ({ projectId }) => {
  const [rounds, setRounds] = useState<Round[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [stages, setStages] = useState<{ id: string; name: string }[]>([]);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);

  const loadRounds = async () => {
    const cacheKey = `safety_rounds:${projectId}`;
    const { data, error } = await supabase
      .from("safety_rounds")
      .select("id, name, round_type, round_date, area, participants, status, notes, stage_id")
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });
    let list: Round[];
    if (error) {
      const cached = cacheGet<Round[]>(cacheKey);
      if (cached) {
        toast.info("Visar sparad kopia (offline)");
        list = cached;
      } else {
        toast.error("Kunde inte läsa skyddsronderna");
        list = [];
      }
    } else {
      list = (data || []) as Round[];
      cacheSet(cacheKey, list);
    }
    setRounds(list);
    setActiveId((prev) => prev ?? list[0]?.id ?? null);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    loadRounds();
    supabase
      .from("project_stages")
      .select("id, name")
      .eq("project_id", projectId)
      .order("created_at", { ascending: true })
      .then(({ data }) => setStages(data || []));
    supabase
      .from("projects")
      .select("name")
      .eq("id", projectId)
      .maybeSingle()
      .then(({ data }) => setProjectName(data?.name ?? ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    const loadItems = async () => {
      if (!activeId) return setItems([]);
      const cacheKey = `safety_round_items:${activeId}`;
      const { data, error } = await supabase
        .from("safety_round_items")
        .select("*")
        .eq("round_id", activeId)
        .order("sort_order", { ascending: true });
      if (error) {
        const cached = cacheGet<Item[]>(cacheKey);
        if (cached) setItems(cached);
        else toast.error("Kunde inte läsa kontrollpunkter");
      } else {
        const list = (data || []) as Item[];
        setItems(list);
        cacheSet(cacheKey, list);
      }
    };
    loadItems();
  }, [activeId]);

  const createRound = async (type: SafetyRoundType) => {
    const today = new Date().toISOString().slice(0, 10);
    const count = rounds.filter((r) => r.round_type === type).length + 1;
    const name =
      type === "overlamnande"
        ? `Överlämnande skyddsrond ${today}`
        : `Skyddsrond ${count} – ${today}`;

    const roundId = newLocalId();
    const template = type === "overlamnande" ? HANDOVER_SAFETY_ROUND_TEMPLATE : SAFETY_ROUND_TEMPLATE;
    let order = 0;
    const rows = template.flatMap((sec) =>
      sec.items.map((it) => ({
        id: newLocalId(),
        round_id: roundId,
        section: sec.section,
        item_key: it.key,
        control_point: it.controlPoint,
        legal_reference: it.legalReference ?? null,
        explanation: it.explanation ?? null,
        status: "ej_kontrollerad",
        sort_order: order++,
      }))
    );

    const roundRow = {
      id: roundId,
      project_id: projectId,
      name,
      round_type: type,
      round_date: today,
      area: null,
      participants: null,
      status: "draft",
      notes: null,
    };

    const res = await offlineInsert("safety_rounds", roundRow);
    await offlineInsert("safety_round_items", rows);

    const newRound = roundRow as unknown as Round;
    setRounds((prev) => {
      const next = [...prev, newRound];
      cacheSet(`safety_rounds:${projectId}`, next);
      return next;
    });
    setItems(rows as unknown as Item[]);
    cacheSet(`safety_round_items:${roundId}`, rows);
    setActiveId(roundId);
    toast.success(res.queued ? "Skyddsrond skapad offline – synkas senare" : "Skyddsrond skapad");
  };

  const removeRound = async (id: string) => {
    setRounds((prev) => {
      const next = prev.filter((r) => r.id !== id);
      cacheSet(`safety_rounds:${projectId}`, next);
      return next;
    });
    setActiveId(null);
    await offlineDelete("safety_rounds", id);
  };

  const updateRound = async (id: string, patch: Partial<Round>) => {
    setRounds((prev) => {
      const next = prev.map((r) => (r.id === id ? { ...r, ...patch } : r));
      cacheSet(`safety_rounds:${projectId}`, next);
      return next;
    });
    await offlineUpdate("safety_rounds", id, patch as Record<string, unknown>);
  };

  const updateItem = async (id: string, patch: Partial<Item>) => {
    setItems((prev) => {
      const next = prev.map((i) => (i.id === id ? { ...i, ...patch } : i));
      if (activeId) cacheSet(`safety_round_items:${activeId}`, next);
      return next;
    });
    await offlineUpdate("safety_round_items", id, patch as Record<string, unknown>);
  };


  const exportWord = async () => {
    const active = rounds.find((r) => r.id === activeId);
    if (!active) return;
    try {
      await generateSafetyRoundWordDocument({
        id: active.id,
        name: active.name,
        round_type: active.round_type,
        round_date: active.round_date,
        area: active.area,
        participants: active.participants,
        status: active.status,
        notes: active.notes,
        items: items.map((i) => ({
          section: i.section,
          control_point: i.control_point,
          legal_reference: i.legal_reference,
          status: i.status,
          responsible: i.responsible,
          due_date: i.due_date,
          comment: i.comment,
        })),
      }, projectName);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  const active = rounds.find((r) => r.id === activeId) || null;
  const checked = items.filter((i) => i.status !== "unanswered").length;
  const deviations = items.filter((i) => i.status === "deviation" || i.status === "action_planned").length;
  const progress = items.length ? Math.round((checked / items.length) * 100) : 0;
  const sections = Array.from(new Set(items.map((i) => i.section)));

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar skyddsronder...</div>;

  return (
    <div className="space-y-4">
      <OfflineBanner onSynced={loadRounds} />

      <DocumentHeader
        docNumber="27"
        exportHistory={{ documentType: "skyddsrond" }}
        title="Skyddsrondsprotokoll"
        description="Bas-U genomför regelbundna skyddsronder med entreprenörer och skyddsombud. Den överlämnande skyddsronden genomförs innan bruksskedet och stänger cirkeln mot Dok 33 och Dok 34."
        icon={HardHat}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => createRound("ordinarie")}>
              <Plus className="h-4 w-4 mr-1" />Skyddsrond
            </Button>
            <Button variant="outline" onClick={() => createRound("overlamnande")}>
              <CheckCircle2 className="h-4 w-4 mr-1" />Överlämnande rond
            </Button>
            {activeId && (
              <Button variant="outline" onClick={exportWord}>
                <Download className="h-4 w-4 mr-1" />Word
              </Button>
            )}
          </div>
        }
      />

      {rounds.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <HardHat className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Ingen skyddsrond genomförd ännu. Skapa en rond för att protokollföra kontrollen på arbetsplatsen.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {rounds.map((r) => (
              <Button
                key={r.id}
                size="sm"
                variant={activeId === r.id ? "default" : "outline"}
                onClick={() => setActiveId(r.id)}
              >
                {r.name}
              </Button>
            ))}
          </div>

          {active && (
            <Card>
              <CardHeader className="pb-3 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    {active.name}
                    <Badge variant="secondary" className="text-[10px]">
                      {SAFETY_ROUND_TYPE_LABELS[(active.round_type as SafetyRoundType) ?? "ordinarie"]}
                    </Badge>
                    {deviations > 0 && (
                      <Badge variant="destructive" className="text-[10px]">{deviations} avvikelser</Badge>
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    <div className="w-40">
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>Kontrollerat</span><span>{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeRound(active.id)}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Datum</label>
                    <Input
                      type="date"
                      defaultValue={active.round_date ?? ""}
                      onBlur={(e) => updateRound(active.id, { round_date: e.target.value || null })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Område</label>
                    <Input
                      placeholder="t.ex. Plan 3, stomme"
                      defaultValue={active.area ?? ""}
                      onBlur={(e) => updateRound(active.id, { area: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Deltagare</label>
                    <Input
                      placeholder="Bas-U, skyddsombud, entreprenörer"
                      defaultValue={active.participants ?? ""}
                      onBlur={(e) => updateRound(active.id, { participants: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Etapp</label>
                    <Select
                      value={active.stage_id ?? "none"}
                      onValueChange={(v) =>
                        updateRound(active.id, { stage_id: v === "none" ? null : v })
                      }
                      disabled={stages.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Hela projektet" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Hela projektet</SelectItem>
                        {stages.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {sections.map((section) => (
                  <div key={section} className="space-y-3">
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                      {section}
                    </h4>
                    <ul className="divide-y">
                      {items.filter((i) => i.section === section).map((item) => (
                        <li key={item.id} className="py-3 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{item.control_point}</span>
                            {item.legal_reference && (
                              <Badge variant="secondary" className="text-[10px]">{item.legal_reference}</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(Object.keys(SAFETY_ROUND_STATUS_LABELS) as SafetyRoundStatus[]).map((s) => (
                              <Button
                                key={s}
                                size="sm"
                                variant={
                                  item.status === s ? (s === "deviation" ? "destructive" : "default") : "outline"
                                }
                                onClick={() => updateItem(item.id, { status: s })}
                              >
                                {SAFETY_ROUND_STATUS_LABELS[s]}
                              </Button>
                            ))}
                          </div>
                          <div className="grid gap-2 sm:grid-cols-4">
                            <Input
                              placeholder="Ansvarig"
                              defaultValue={item.responsible ?? ""}
                              onBlur={(e) => updateItem(item.id, { responsible: e.target.value })}
                            />
                            <Input
                              type="date"
                              defaultValue={item.due_date ?? ""}
                              onBlur={(e) => updateItem(item.id, { due_date: e.target.value || null })}
                            />
                            <AiPolishTextarea
                              className="sm:col-span-2"
                              rows={2}
                              placeholder="Kommentar / åtgärd"
                              context="Anmärkning och åtgärd i skyddsrondsprotokoll"
                              defaultValue={item.comment ?? ""}
                              onCommit={(v) => updateItem(item.id, { comment: v })}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Sammanfattande noteringar</label>
                  <AiPolishTextarea
                    rows={3}
                    placeholder="Sammanfattning, beslut och nästa steg"
                    context="Sammanfattning av skyddsrond"
                    defaultValue={active.notes ?? ""}
                    onCommit={(v) => updateRound(active.id, { notes: v })}
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    variant={active.status === "signed" ? "outline" : "default"}
                    onClick={() =>
                      updateRound(active.id, { status: active.status === "signed" ? "draft" : "signed" })
                    }
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    {active.status === "signed" ? "Öppna igen" : "Markera som genomförd"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default SafetyRounds;
